import React, { useState, useEffect } from 'react';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import { supabase } from '../supabaseClient';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Reflection({ goBack, goHome, handleSaveProgress }) {
    // Card picker state
    const [cards, setCards] = useState([]);
    const [shuffledCards, setShuffledCards] = useState([]);
    const [selectedCards, setSelectedCards] = useState([]);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [clickedCardId, setClickedCardId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedReadings, setSavedReadings] = useState([]);
    const [cardsLoaded, setCardsLoaded] = useState(false);

    // Fetch cards for 'words' component
    useEffect(() => {
        const fetchCards = async () => {
            const { data, error } = await supabase
                .schema('public')
                .from('cards')
                .select('id, title, image_path, descriptions')
                .eq('component', 'words');
            if (error) {
                toast.error('Failed to load cards.');
                setCardsLoaded(false);
                return;
            }
            setCards(data || []);
            setShuffledCards([...(data || [])]);
            setCardsLoaded(true);
        };
        fetchCards();
        fetchSavedReadings();
        // eslint-disable-next-line
    }, []);

    // Fetch saved readings for this component
    const fetchSavedReadings = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await supabase
                .schema('public')
                .from('card_readings')
                .select('*')
                .eq('user_id', user.id)
                .eq('component', 'words')
                .order('created_at', { ascending: false });
            if (!error) setSavedReadings(data || []);
        } catch (err) {
            // ignore
        }
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Card click handler
    const handleCardClick = (card) => {
        // Allow up to 6 cards
        if (selectedCards.length < 6 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    // Start reading
    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible(!pickerVisible);
        if (cards.length > 0) setShuffledCards(shuffleArray([...cards]));
    };

    // Save reading
    const handleSaveReading = async () => {
        if (selectedCards.length !== 6) {
            toast.error('Please select exactly 6 cards to save your reading.');
            return;
        }
        setSaving(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                toast.error('User not logged in.');
                setSaving(false);
                return;
            }
            const readingData = {
                user_id: user.id,
                component: 'words',
                cards: selectedCards.map(card => card.id),
                descriptions: selectedCards.reduce((acc, card, idx) => {
                    acc[`position${idx + 1}`] = card.descriptions || 'No description';
                    return acc;
                }, {}),
            };
            const { error } = await supabase
                .schema('public')
                .from('card_readings')
                .insert(readingData);
            if (error) throw error;
            toast.success('Reading saved!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save reading.');
        } finally {
            setSaving(false);
        }
    };

    // Load reading
    const handleLoadReading = (reading) => {
        if (!cardsLoaded || cards.length === 0) {
            toast.info('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = reading.cards.map((cardId) => {
            const card = cardsById[cardId];
            if (!card) {
                console.warn(`Card ID ${cardId} not found while loading saved reading.`);
                return null;
            }
            return card;
        }).filter(Boolean);

        if (loadedCards.length !== reading.cards.length) {
            toast.error('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards);
        setPickerVisible(false);
    };

    // Delete reading
    const handleDeleteReading = async (readingId) => {
        await supabase
            .schema('public')
            .from('card_readings')
            .delete()
            .eq('id', readingId);
        await fetchSavedReadings();
    };

    // Responsive: detect mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

    // Card position titles for reading display
    const cardPositions = [
        'Balance achieved', 'Aspect of self/life to grow/that are growing', 'Let go of',
        'Fixed mindset', 'What needs to be seen', 'Your needs '
    ];

    // Card aspect ratio for business card (5:3)
    const cardAspectWidth = 180;
    const cardAspectHeight = 108;

    // Helper to filter out back images from card set
    const isNotBackImage = (card) => {
        if (!card.image_path) return true;
        return !card.image_path.toLowerCase().includes('wbackimage');
    };

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px',
        }}>
            <ToastContainer />
            {/* Title */}
            <h3 style={{
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '32px',
            }}>
                Reflection
            </h3>

            {/* Session Helper Cards Row */}
            <div
                className="reflection-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• Being aware of what we are thinking and feeling opens the door to so much more.</div>
                        <div>• Everything we observe outside of us has the ability to inform us about what is inside of us.</div>
                        <div>• Looking over the information we have can help us see new things.</div>
                        <div>• Retrospect can be vital for clearing up the misunderstandings we may be carrying.</div>
                        <div>• We tend to show ourselves outwardly in accordance to how we think of ourselves.</div>
                        <div>• Sometimes it is easier to see something about ourselves when we see it outside of ourselves first.</div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. Who matters in this experience?</div>
                        <div>2. What matters in this experience?</div>
                        <div>3. When did this matter?</div>
                        <div>4. Where did this matter?</div>
                        <div>5. Why did this matter?</div>
                        <div>6. How did this matter to you?</div>
                    </div>
                </div>
            </div>

            {/* Cards */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                maxWidth: '600px',
            }}>
                {/* Card 1 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Take time to reflect on your thoughts, actions, and experiences.
                    </p>
                </div>

                {/* Card 2 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Learn how reflection can help you gain clarity and insight.
                    </p>
                </div>

                {/* Card 3 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Reflect on how self-awareness can lead to personal growth.
                    </p>
                </div>
            </div>

            {/* Card Picker */}
            {pickerVisible && (
                <div
                    style={{
                        width: '100vw',
                        maxWidth: '100vw',
                        padding: '8px 0',
                        margin: isMobile ? '0 auto' : '0 auto',
                        minHeight: isMobile ? '480px' : '360px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {(() => {
                        const rows = isMobile ? 4 : 3;
                        const cardsPerRow = isMobile ? 38 : 50;
                        const totalRows = Array.from({ length: rows });
                        // For mobile, calculate overlap so the last card is fully visible and the row fits the screen
                        const containerWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
                        const effectiveWidth = Math.min(containerWidth, 0.98 * containerWidth);
                        const pickerCardWidth = isMobile ? cardAspectHeight : cardAspectWidth;
                        const pickerCardHeight = isMobile ? cardAspectWidth : cardAspectHeight;
                        const overlap = cardsPerRow > 1
                            ? Math.floor((pickerCardWidth * cardsPerRow - effectiveWidth) / (cardsPerRow - 1))
                            : 0;

                        return totalRows.map((_, rowIdx) => {
                            const rowCards = shuffledCards
                                .filter(isNotBackImage)
                                .slice(rowIdx * cardsPerRow, (rowIdx + 1) * cardsPerRow);
                            if (isMobile) {
                                // Absolute positioning for fanned overlap, starting at left edge
                                const rowWidth = pickerCardWidth + (rowCards.length - 1) * (pickerCardWidth - overlap);
                                return (
                                    <div
                                        key={rowIdx}
                                        style={{
                                            position: 'relative',
                                            width: `${Math.min(rowWidth, effectiveWidth)}px`,
                                            height: `${pickerCardHeight}px`,
                                            margin: rowIdx === 0 ? '0 auto 8px auto' : '8px auto 0 auto',
                                            display: 'block',
                                            maxWidth: '100vw',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {rowCards.map((card, idx) => (
                                            <div
                                                key={card.id}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${idx * (pickerCardWidth - overlap)}px`,
                                                    top: 0,
                                                    width: `${pickerCardWidth}px`,
                                                    height: `${pickerCardHeight}px`,
                                                    cursor: 'pointer',
                                                    boxShadow: 'none',
                                                    transition: 'transform 0.2s',
                                                    transform: `rotate(90deg)${clickedCardId === card.id ? ' scale(1.1)' : ''}`,
                                                    zIndex: idx,
                                                    borderRadius: '8px',
                                                    background: 'none',
                                                    overflow: 'visible',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/words/wbackimage01.webp)',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    backgroundSize: 'contain',
                                                }}
                                                onClick={() => handleCardClick(card)}
                                            />
                                        ))}
                                    </div>
                                );
                            } else {
                                // ...existing browser view logic...
                                const containerWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
                                const effectiveWidth = Math.min(containerWidth, 0.98 * containerWidth);
                                const rowWidth = pickerCardWidth + (rowCards.length - 1) * (pickerCardWidth - overlap);
                                return (
                                    <div
                                        key={rowIdx}
                                        style={{
                                            position: 'relative',
                                            width: `${Math.min(rowWidth, effectiveWidth)}px`,
                                            height: `${pickerCardHeight}px`,
                                            margin: rowIdx === 0 ? '0 auto 16px auto' : '16px auto 0 auto',
                                            display: 'block',
                                            maxWidth: '100vw',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {rowCards.map((card, idx) => (
                                            <div
                                                key={card.id}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${idx * (pickerCardWidth - overlap)}px`,
                                                    top: 0,
                                                    width: `${pickerCardWidth}px`,
                                                    height: `${pickerCardHeight}px`,
                                                    cursor: 'pointer',
                                                    boxShadow: clickedCardId === card.id
                                                        ? '0 0 10px rgba(0, 255, 0, 0.5)'
                                                        : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    transform: clickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                                    zIndex: idx,
                                                    borderRadius: '8px',
                                                    background: 'none',
                                                    overflow: 'visible',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/words/wbackimage01.webp)',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    backgroundSize: 'contain',
                                                }}
                                                onClick={() => handleCardClick(card)}
                                            />
                                        ))}
                                    </div>
                                );
                            }
                        });
                    })()}
                </div>
            )}

            {/* Start Card Reading button */}
            <button
                style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: 'auto',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    margin: '24px auto 0 auto',
                    position: 'relative',
                }}
                onClick={handleStartReading}
            >
                <span style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                    This feature is a novelty and not required for your experience. You may skip this card picker.
                </span>
                {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
            </button>

            {/* Selected Cards (Reading Display) */}
            {selectedCards.filter(isNotBackImage).length > 0 && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                        gridTemplateRows: isMobile ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                        gap: '12px',
                        marginTop: '16px',
                        background: 'white',
                        border: '2px solid black',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '12px',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '16px auto',
                        width: '100%',
                    }}
                >
                    {selectedCards.filter(isNotBackImage).slice(0, isMobile ? 6 : 6).map((card, idx) => (
                        <div key={card.id} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                            background: '#f8f8f8',
                            borderRadius: '8px',
                            padding: '8px',
                        }}>
                            <div style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#4B5563',
                                marginBottom: '4px',
                                textAlign: 'center',
                            }}>
                                {cardPositions[idx]}
                            </div>
                            <img
                                src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                alt=""
                                style={{
                                    width: isMobile ? `${cardAspectWidth * 1.1}px` : `${cardAspectWidth * 1.1}px`,
                                    height: isMobile ? `${cardAspectHeight * 1.1}px` : `${cardAspectHeight * 1.1}px`,
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    marginBottom: '6px',
                                    background: '#fff',
                                }}
                            />
                            <div style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '12px',
                                color: '#333',
                                marginTop: '2px',
                                textAlign: 'center',
                                maxWidth: '120px',
                                whiteSpace: 'pre-line',
                            }}>
                                {card.descriptions || 'No description'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Save Reading button */}
            {selectedCards.filter(isNotBackImage).length === 6 && (
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                        position: 'relative',
                    }}
                    onClick={handleSaveReading}
                    disabled={saving}
                >
                    <span style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                        This feature is a novelty and not required for your experience. You may skip this card picker.
                    </span>
                    {saving ? 'Saving...' : 'Save Reading'}
                </button>
            )}

            {/* Navigation Buttons */}
            <div
                className="reflection-nav-btn-row"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '16px',
                    marginTop: '32px',
                    marginBottom: '16px',
                }}
            >
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={goBack}>Back</button>
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={goHome}>Home</button>
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={handleSaveProgress}>Save</button>
            </div>

            {/* Saved Readings */}
            {savedReadings.length > 0 && (
                <div
                    style={{
                        marginTop: '32px',
                        width: '100%',
                        maxWidth: '600px',
                        padding: '8px',
                    }}
                >
                    <h4
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#4B5563',
                            textAlign: 'center',
                            marginBottom: '16px',
                        }}
                    >
                        Saved Readings
                    </h4>
                    {savedReadings.map((reading) => (
                        <div
                            key={reading.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                border: '1px solid black',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                                color: '#4B5563',
                                background: '#fff',
                            }}
                        >
                            <span onClick={() => handleLoadReading(reading)}>
                                {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    marginLeft: '16px',
                                }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleDeleteReading(reading.id);
                                }}
                            >
                                -delete
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <style>
                {`
                @media (max-width: 600px) {
                    .reflection-nav-btn-row {
                        position: static !important;
                        width: 100%;
                        background: none;
                        border-top: none;
                        border-radius: 0;
                        box-shadow: none;
                        padding: 0;
                        z-index: auto;
                        display: flex;
                        justify-content: center;
                        gap: 10px;
                        margin-top: 32px !important;
                        margin-bottom: 16px !important;
                    }
                    body {
                        padding-bottom: 0 !important;
                    }
                }
                `}
            </style>
        </div>
    );
}

export default Reflection;
